import { createContext, useCallback, useEffect, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import type { Medialink } from '../../../../api/medialink';
import type { SlideSet } from '../../../../api/slide-set';
import type { UserMaterial } from '../../../../api/user-material';

type SelectionContextType = {
  showSelection: boolean;
  toggleShowSelection: () => void;
  selectedMedialinks: Medialink[];
  selectedSlideSets: SlideSet[];
  selectedUserMaterials: UserMaterial[];
  addMedialinks: (materials: Medialink[]) => void;
  addSlideSets: (materials: SlideSet[]) => void;
  addUserMaterials: (materials: UserMaterial[]) => void;
  removeMedialinks: (materials: Medialink[]) => void;
  removeSlideSets: (materials: SlideSet[]) => void;
  removeUserMaterials: (materials: UserMaterial[]) => void;
  countSelection: () => number;
  clearSelection: () => void;
};

const SelectionContext = createContext<SelectionContextType>({
  showSelection: false,
  toggleShowSelection: () => {},
  selectedMedialinks: [],
  selectedSlideSets: [],
  selectedUserMaterials: [],
  addMedialinks: () => {},
  addSlideSets: () => {},
  addUserMaterials: () => {},
  removeMedialinks: () => {},
  removeSlideSets: () => {},
  removeUserMaterials: () => {},
  countSelection: () => 0,
  clearSelection: () => {},
});

export default SelectionContext;

export function SelectionContextProvider({ children }: PropsWithChildren) {
  const [showSelection, setShowSelection] = useState<boolean>(false);

  const [selectedMedialinks, setSelectedMedialinks] = useState<Medialink[]>([]);
  const [selectedSlideSets, setSelectedSlideSets] = useState<SlideSet[]>([]);
  const [selectedUserMaterials, setSelectedUserMaterials] = useState<UserMaterial[]>([]);

  const location = useLocation();

  const toggleShowSelection = () => {
    if (showSelection) clearSelection();

    setShowSelection(prev => !prev);
  };

  const addMedialinks = (medialinks: Medialink[]) => {
    setSelectedMedialinks(prev => [...prev, ...medialinks.filter(m => !prev.includes(m))]);
  };

  const addSlideSets = (slideSets: SlideSet[]) => {
    setSelectedSlideSets(prev => [...prev, ...slideSets.filter(s => !prev.includes(s))]);
  };

  const addUserMaterials = (userMaterials: UserMaterial[]) => {
    setSelectedUserMaterials(prev => [...prev, ...userMaterials.filter(um => !prev.includes(um))]);
  };

  const removeMedialinks = (medialinks: Medialink[]) => {
    setSelectedMedialinks(prev => prev.filter(m => !medialinks.includes(m)));
  };

  const removeSlideSets = (slideSets: SlideSet[]) => {
    setSelectedSlideSets(prev => prev.filter(s => !slideSets.includes(s)));
  };

  const removeUserMaterials = (userMaterials: UserMaterial[]) => {
    setSelectedUserMaterials(prev => prev.filter(um => !userMaterials.includes(um)));
  };

  const clearSelection = () => {
    setSelectedMedialinks([]);
    setSelectedSlideSets([]);
    setSelectedUserMaterials([]);
  };

  const countSelection = useCallback(
    () => selectedMedialinks.length + selectedSlideSets.length + selectedUserMaterials.length,
    [selectedMedialinks, selectedSlideSets, selectedUserMaterials],
  );

  useEffect(() => {
    if (showSelection) toggleShowSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <SelectionContext.Provider
      value={{
        showSelection,
        toggleShowSelection,
        selectedUserMaterials,
        selectedSlideSets,
        selectedMedialinks,
        addUserMaterials,
        addSlideSets,
        addMedialinks,
        removeUserMaterials,
        removeSlideSets,
        removeMedialinks,
        countSelection,
        clearSelection,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
}
